import { cheaper, PRICES } from './prices.ts';
import { advance, dayOf, earn, money, spend, type LifeEvent, type LifeState } from './life.ts';
import { Rng } from './rng.ts';
import { nudge } from './character.ts';
import { decorComfort, decorUp } from './decor.ts';
import { US } from './content/us.ts';
import { CHINA } from './content/china.ts';
import { JAPAN } from './content/japan.ts';
import { LAGOS } from './content/lagos.ts';
import { FRANCE } from './content/france.ts';
import { ITALY, UAE } from './content/italydubai.ts';
import type { RegionContent } from './content/types.ts';
import { USD_NGN, type Country } from './travel.ts';

/**
 * Where you live, and what it's like inside. You can rent or buy somewhere else (after going to
 * see it), and make it yours: renters can furnish (the landlord won't let you touch the walls),
 * owners can repaint and re-floor too. What you put in makes home more comfortable, and comfort
 * shows up in how well you sleep and how good an evening in feels.
 */

export type Layout = 'face_me' | 'self_contain' | 'mini_flat' | 'family';

export interface HomeStyle {
  wall: string;
  floor: string;
  sofa: string;
  bed: string;
  curtains: string;
  rug: string;
  tv: string;
  extras: string[]; // 'plants' | 'art' | 'ac' | 'lamp' | 'easel'
  /** the room's bones (renovations: owners only): the ceiling, what's on the walls besides paint, the bathroom, the kitchen */
  ceiling: string;
  finish: string;
  bath: string;
  kitchen: string;
  /** the light fittings (anyone: you take them with you) */
  lighting: string;
  /** where the furniture stands: 'a' as it came, 'b' the other way round */
  arrange: string;
  /** outside: the paint on the building, the gate, the generator, the compound */
  paint: string;
  gate: string;
  generator: string;
  garden: string;
  /** a house of your own (the country house): the shutters, the roof, the front door */
  shutters: string;
  roof: string;
  door: string;
}
/** (a saved style also has `room`, what's on the walls and floor from decor.ts, in step with what you own, and
 * `roomDown`, what you've taken down: not style options, so not in HomeStyle's keys) */
export type RoomStyle = HomeStyle & { room?: string[]; roomDown?: string[] };

export interface StyleOption { id: string; label: string; price: number; colour?: number; comfort: number; ownersOnly?: boolean }
export const STYLE: Record<Exclude<keyof HomeStyle, 'extras'>, StyleOption[]> & { extras: StyleOption[] } = {
  paint: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'cream', label: 'Cream', price: 380_000, colour: 0xefe2c4, comfort: 2, ownersOnly: true },
    { id: 'white', label: 'Brilliant white', price: 420_000, colour: 0xf4f2ec, comfort: 2, ownersOnly: true },
    { id: 'sky', label: 'Sky blue', price: 400_000, colour: 0xb9d3e0, comfort: 2, ownersOnly: true },
    { id: 'peach', label: 'Peach', price: 400_000, colour: 0xf0c49c, comfort: 2, ownersOnly: true },
    { id: 'grey', label: 'Modern grey', price: 520_000, colour: 0x8a8d92, comfort: 3, ownersOnly: true },
    { id: 'butter', label: 'Butter yellow', price: 400_000, colour: 0xf2dc94, comfort: 2, ownersOnly: true },
    { id: 'sage', label: 'Sage green', price: 420_000, colour: 0xa9bc9a, comfort: 2, ownersOnly: true },
    { id: 'blush', label: 'Blush pink', price: 400_000, colour: 0xeec2bc, comfort: 2, ownersOnly: true },
    { id: 'navy', label: 'Navy', price: 520_000, colour: 0x34445e, comfort: 3, ownersOnly: true },
    { id: 'barn', label: 'Barn red', price: 480_000, colour: 0x8e3428, comfort: 2, ownersOnly: true },
    { id: 'charcoal', label: 'Charcoal', price: 560_000, colour: 0x3c3e42, comfort: 3, ownersOnly: true },
  ],
  shutters: [
    { id: 'default', label: 'As it came (navy blue)', price: 0, comfort: 0 },
    { id: 'black', label: 'Black', price: 160_000, colour: 0x1e1f22, comfort: 1, ownersOnly: true },
    { id: 'green', label: 'Hunter green', price: 160_000, colour: 0x24412e, comfort: 1, ownersOnly: true },
    { id: 'red', label: 'Burgundy', price: 160_000, colour: 0x6a1e24, comfort: 1, ownersOnly: true },
    { id: 'sky', label: 'Haint blue', price: 160_000, colour: 0x9cc6d0, comfort: 1, ownersOnly: true },
    { id: 'white', label: 'White', price: 140_000, colour: 0xf2f0ea, comfort: 1, ownersOnly: true },
    { id: 'none', label: 'Take them off', price: 40_000, comfort: 0, ownersOnly: true },
  ],
  roof: [
    { id: 'default', label: 'As it came (red-brown shingle)', price: 0, comfort: 0 },
    { id: 'slate', label: 'Slate grey', price: 2_400_000, colour: 0x6a6e74, comfort: 2, ownersOnly: true },
    { id: 'black', label: 'Black', price: 2_400_000, colour: 0x2c2c30, comfort: 2, ownersOnly: true },
    { id: 'green', label: 'Forest green', price: 2_600_000, colour: 0x3a5a40, comfort: 2, ownersOnly: true },
    { id: 'copper', label: 'Weathered copper', price: 4_800_000, colour: 0x5e9a88, comfort: 3, ownersOnly: true },
    { id: 'terracotta', label: 'Terracotta', price: 2_600_000, colour: 0xc0663e, comfort: 2, ownersOnly: true },
  ],
  door: [
    { id: 'default', label: 'As it came (white)', price: 0, comfort: 0 },
    { id: 'red', label: 'Red', price: 60_000, colour: 0xa8232a, comfort: 1, ownersOnly: true },
    { id: 'black', label: 'Black gloss', price: 60_000, colour: 0x18181a, comfort: 1, ownersOnly: true },
    { id: 'navy', label: 'Navy', price: 60_000, colour: 0x22304e, comfort: 1, ownersOnly: true },
    { id: 'yellow', label: 'Yellow', price: 60_000, colour: 0xe8b53a, comfort: 1, ownersOnly: true },
    { id: 'green', label: 'Green', price: 60_000, colour: 0x2a5a3a, comfort: 1, ownersOnly: true },
    { id: 'oak', label: 'Stained oak', price: 120_000, colour: 0x7a4e2a, comfort: 1, ownersOnly: true },
  ],
  gate: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'black', label: 'Black wrought iron', price: 280_000, colour: 0x1c1c1c, comfort: 2, ownersOnly: true },
    { id: 'green', label: 'Bottle green', price: 260_000, colour: 0x1f4d2b, comfort: 2, ownersOnly: true },
    { id: 'gold', label: 'Black and gold', price: 450_000, colour: 0x8a6a1f, comfort: 3, ownersOnly: true },
  ],
  generator: [
    { id: 'none', label: 'No generator (NEPA only)', price: 0, comfort: 0 },
    { id: 'small', label: '"I better pass my neighbour" petrol gen', price: 120_000, comfort: 5 },
    { id: 'big', label: 'Soundproof diesel generator', price: 1_400_000, comfort: 12 },
  ],
  garden: [
    { id: 'none', label: 'Bare compound', price: 0, comfort: 0 },
    { id: 'plants', label: 'Flower pots and a palm', price: 90_000, comfort: 2, ownersOnly: true },
    { id: 'interlock', label: 'Interlocking tiles and trees', price: 650_000, comfort: 4, ownersOnly: true },
  ],
  wall: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'white', label: 'Gallery white', price: 45_000, colour: 0xf4f3ef, comfort: 2, ownersOnly: true },
    { id: 'cream', label: 'Cream', price: 45_000, colour: 0xeee3c8, comfort: 2, ownersOnly: true },
    { id: 'sage', label: 'Sage green', price: 45_000, colour: 0xa9bfa0, comfort: 2, ownersOnly: true },
    { id: 'sky', label: 'Sky blue', price: 45_000, colour: 0xa8c8dc, comfort: 2, ownersOnly: true },
    { id: 'terracotta', label: 'Terracotta', price: 55_000, colour: 0xc98a6a, comfort: 3, ownersOnly: true },
    { id: 'charcoal', label: 'Charcoal feature', price: 60_000, colour: 0x4a4a50, comfort: 3, ownersOnly: true },
  ],
  floor: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'white_tiles', label: 'White ceramic tiles', price: 180_000, colour: 0xe4e2dc, comfort: 4, ownersOnly: true },
    { id: 'terrazzo', label: 'Terrazzo', price: 260_000, colour: 0xb8b0a4, comfort: 5, ownersOnly: true },
    { id: 'wood', label: 'Wood-look tiles', price: 320_000, colour: 0x9a6e48, comfort: 6, ownersOnly: true },
    { id: 'marble', label: 'Polished marble-look', price: 520_000, colour: 0xd8d4cc, comfort: 8, ownersOnly: true },
    { id: 'concrete', label: 'Polished concrete', price: 300_000, colour: 0x8e8c88, comfort: 5, ownersOnly: true },
    { id: 'herringbone', label: 'Oak herringbone parquet', price: 640_000, colour: 0x9a7048, comfort: 8, ownersOnly: true },
  ],
  ceiling: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'pop', label: 'POP ceiling with downlights', price: 350_000, comfort: 4, ownersOnly: true },
    { id: 'coffered', label: 'Coffered ceiling (deep white squares)', price: 780_000, comfort: 6, ownersOnly: true },
    { id: 'beams', label: 'Exposed dark wood beams', price: 520_000, comfort: 5, ownersOnly: true },
    { id: 'cove', label: 'Floating ceiling, warm LED cove light', price: 640_000, comfort: 6, ownersOnly: true },
  ],
  lighting: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'pendants', label: 'Three black-and-brass pendants', price: 120_000, comfort: 3 },
    { id: 'chandelier', label: 'Crystal chandelier', price: 900_000, comfort: 7 },
    { id: 'track', label: 'Track spotlights', price: 160_000, comfort: 3 },
    { id: 'lantern', label: 'Paper lantern', price: 30_000, comfort: 2 },
  ],
  finish: [
    { id: 'default', label: 'Just paint', price: 0, comfort: 0 },
    { id: 'panel', label: 'Wood panelling, waist height, all round', price: 420_000, comfort: 4, ownersOnly: true },
    { id: 'slats', label: 'Vertical oak slats (a feature wall)', price: 380_000, comfort: 4, ownersOnly: true },
    { id: 'stone', label: 'Stacked stone (a feature wall)', price: 560_000, comfort: 5, ownersOnly: true },
    { id: 'brick', label: 'Exposed brick (a feature wall)', price: 300_000, comfort: 3, ownersOnly: true },
    { id: 'stripes', label: 'Striped wallpaper, all round', price: 160_000, comfort: 3, ownersOnly: true },
  ],
  bath: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'shower', label: 'Glass shower cabin, rain head', price: 450_000, comfort: 4, ownersOnly: true },
    { id: 'tub', label: 'Bathtub', price: 700_000, comfort: 6, ownersOnly: true },
    { id: 'jacuzzi', label: 'Jacuzzi', price: 4_500_000, comfort: 10, ownersOnly: true },
  ],
  kitchen: [
    { id: 'default', label: 'As it came', price: 0, comfort: 0 },
    { id: 'fitted', label: 'Fitted white kitchen, wall cupboards', price: 1_200_000, comfort: 5, ownersOnly: true },
    { id: 'gloss', label: 'Black gloss kitchen, gold handles, extractor hood', price: 2_600_000, comfort: 8, ownersOnly: true },
  ],
  arrange: [
    { id: 'a', label: 'As it was set up', price: 0, comfort: 0 },
    { id: 'b', label: 'The other way round (two lads and a van)', price: 15_000, comfort: 0 },
  ],
  sofa: [
    { id: 'none', label: 'No sofa', price: 0, comfort: 0 },
    { id: 'plastic', label: 'Plastic chairs', price: 18_000, colour: 0xd23a2a, comfort: 1 },
    { id: 'fabric', label: 'Green fabric sofa', price: 140_000, colour: 0x3f6a4a, comfort: 5 },
    { id: 'leather', label: 'Brown leather sofa', price: 260_000, colour: 0x5a3020, comfort: 7 },
    { id: 'lshape', label: 'Grey L-shaped sofa', price: 480_000, colour: 0x7a7a80, comfort: 10 },
  ],
  bed: [
    { id: 'mattress', label: 'Mattress on the floor', price: 0, comfort: 0 },
    { id: 'frame', label: 'Wooden bed frame', price: 95_000, colour: 0x6b4a30, comfort: 5 },
    { id: 'upholstered', label: 'Upholstered bed, orthopaedic mattress', price: 340_000, colour: 0x8a8a90, comfort: 10 },
  ],
  curtains: [
    { id: 'default', label: 'Plain', price: 0, comfort: 0 },
    { id: 'gold', label: 'Gold drapes', price: 25_000, colour: 0xc9a227, comfort: 1 },
    { id: 'ankara', label: 'Ankara print', price: 30_000, colour: 0xc0542a, comfort: 2 },
    { id: 'blackout', label: 'Blackout (sleep better)', price: 38_000, colour: 0x2a2a3a, comfort: 3 },
  ],
  rug: [
    { id: 'none', label: 'No rug', price: 0, comfort: 0 },
    { id: 'red', label: 'Red and gold rug', price: 22_000, colour: 0x7a2e3a, comfort: 1 },
    { id: 'shaggy', label: 'Grey shaggy rug', price: 48_000, colour: 0x9a9aa0, comfort: 2 },
  ],
  tv: [
    { id: 'none', label: 'No TV', price: 0, comfort: 0 },
    { id: 'small', label: '32-inch TV + decoder', price: 160_000, comfort: 4 },
    { id: 'big', label: '65-inch smart TV', price: 720_000, comfort: 8 },
  ],
  extras: [
    { id: 'plants', label: 'Potted plants', price: 15_000, comfort: 1 },
    { id: 'art', label: 'Framed art', price: 40_000, comfort: 1 },
    { id: 'lamp', label: 'Standing lamp', price: 28_000, comfort: 1 },
    { id: 'easel', label: 'Art pack: easel, canvases, paints', price: 35_000, comfort: 1 },
    { id: 'keyboard', label: 'Keyboard (61 keys) on a stand: play it at home', price: 60_000, comfort: 1 },
    { id: 'console', label: 'Games console + two pads (needs a TV)', price: 450_000, comfort: 2 },
    { id: 'pool', label: 'Pool table (needs the space: a flat or a house)', price: 900_000, comfort: 3 },
    { id: 'dog', label: 'A dog (a puppy from a breeder, with a bed and bowls)', price: 150_000, comfort: 3 },
    { id: 'cat', label: 'A cat (a kitten, with a bed, bowls and a scratching post)', price: 60_000, comfort: 3 },
    { id: 'ac', label: 'Split AC unit', price: 480_000, comfort: 8 },
  ],
};
for (const list of Object.values(STYLE)) for (const o of list) o.price = cheaper(o.price, 1_000); // (prices 30% down: prices.ts)

/**
 * A whole look in one go: everything that makes a room read differently (walls, finish, floor, ceiling, lights,
 * furniture, fabrics, kitchen). What it costs is what its parts cost, less 15%; a renter gets only the parts a
 * renter may change (the furniture, the fabrics, the lights).
 */
export interface StyleSet { id: string; label: string; blurb: string; parts: Partial<Omit<HomeStyle, 'extras'>> }
export const STYLE_SETS: StyleSet[] = [
  { id: 'minimal', label: 'Minimal', blurb: 'White walls, concrete floor, a floating ceiling, track spots, a black gloss kitchen.',
    parts: { wall: 'white', finish: 'default', floor: 'concrete', ceiling: 'cove', lighting: 'track', sofa: 'lshape', bed: 'upholstered', curtains: 'blackout', rug: 'none', kitchen: 'gloss' } },
  { id: 'afroluxe', label: 'Afro-luxe', blurb: 'Terracotta and oak slats, marble, a POP ceiling with a crystal chandelier, gold drapes.',
    parts: { wall: 'terracotta', finish: 'slats', floor: 'marble', ceiling: 'pop', lighting: 'chandelier', sofa: 'leather', bed: 'upholstered', curtains: 'gold', rug: 'red', kitchen: 'gloss' } },
  { id: 'industrial', label: 'Industrial', blurb: 'Charcoal and exposed brick, dark beams, pendants over leather.',
    parts: { wall: 'charcoal', finish: 'brick', floor: 'concrete', ceiling: 'beams', lighting: 'pendants', sofa: 'leather', bed: 'frame', curtains: 'blackout', rug: 'shaggy', kitchen: 'fitted' } },
  { id: 'japandi', label: 'Japandi', blurb: 'Cream, oak, a paper lantern, soft fabric, nothing extra.',
    parts: { wall: 'cream', finish: 'slats', floor: 'wood', ceiling: 'default', lighting: 'lantern', sofa: 'fabric', bed: 'frame', curtains: 'default', rug: 'shaggy', kitchen: 'fitted' } },
  { id: 'classic', label: 'Classic', blurb: 'Sage panelling, herringbone parquet, a coffered ceiling, a chandelier.',
    parts: { wall: 'sage', finish: 'panel', floor: 'herringbone', ceiling: 'coffered', lighting: 'chandelier', sofa: 'fabric', bed: 'upholstered', curtains: 'gold', rug: 'red', kitchen: 'fitted' } },
];

/** What a set would change here, and what it costs (`skipped`: the parts a renter can't have). */
export function setQuote(s: LifeState, id: string): { price: number; changes: [keyof HomeStyle, string][]; skipped: number } | null {
  const set = STYLE_SETS.find((x) => x.id === id);
  if (!set) return null;
  const st = homeStyleOf(s);
  let price = 0, skipped = 0;
  const changes: [keyof HomeStyle, string][] = [];
  for (const [k, v] of Object.entries(set.parts) as [Exclude<keyof HomeStyle, 'extras'>, string][]) {
    if (st[k] === v) continue;
    const opt = STYLE[k].find((o) => o.id === v);
    if (!opt) continue;
    if ((opt.ownersOnly && !ownsHome(s)) || (k === 'bath' && !hasBathroom(s))) { skipped++; continue; }
    price += opt.price;
    changes.push([k, v]);
  }
  return { price: Math.round((price * 0.85) / 1000) * 1000, changes, skipped };
}

export function applySet(s: LifeState, id: string): LifeEvent[] | null {
  const q = setQuote(s, id), set = STYLE_SETS.find((x) => x.id === id);
  if (!q || !set || !q.changes.length) return null;
  if (!spend(s, q.price, `${set.label} look`)) return null;
  const st = homeStyleOf(s);
  for (const [k, v] of q.changes) (st as unknown as Record<string, string>)[k] = v;
  s.history.push({ t: s.minutes, text: `Did the house up: the ${set.label.toLowerCase()} look.` });
  return [{ t: s.minutes, kind: 'info', text: `${set.label}. The place looks like somewhere else.` }];
}

/** a face-me-I-face-you room has no bathroom of its own (the bucket, out back) */
export const hasBathroom = (s: LifeState) => s.card.home.id !== 'face_me';

/** Which kind of area a home is in: decides the neighbourhood, the street outside and how it comes furnished. */
const UPPER_AREAS = /Lekki|Ikoyi|Victoria|Ikate|Chevron|Banana|VGC|Eko Atlantic/i;
const MIDDLE_AREAS = /Yaba|Akoka|Surulere|Ikeja|Ogba|Gbagada|Magodo|Festac|Ogudu|Omole/i;
export function homeClass(home: { id: string; area: string }): 'lower' | 'middle' | 'upper' {
  if (home.id === 'country_house') return 'upper';
  if (UPPER_AREAS.test(home.area) || /^(family_estate|lekki_flat)$/.test(home.id) || home.id.startsWith('lux_')) return 'upper';
  if (/Paris|Provence|Aix|Saint-Denis|Marseille/.test(home.area)) return 'middle'; // (France: parquet and plaster, not bare cement)
  if (/Manhattan|Brooklyn|Bronx|Queens|Chicago|San Francisco|Shanghai|Beijing|Huairou|Tokyo|Kyoto|Yamanashi/.test(home.area)) return 'middle'; // (the US, China, Japan: the same)
  if (/^(face_me|family_rented|self_contain)$/.test(home.id)) return 'lower';
  if (/^(mini_flat|family_owned)$/.test(home.id)) return 'middle';
  return MIDDLE_AREAS.test(home.area) ? 'middle' : 'lower';
}

/** A home as it's shown at a viewing (a listing's photos): furnished the way the agent stages it for its class. */
export function stagedStyle(home: { id: string; area: string }): HomeStyle {
  const big = /mini_flat|family|lekki|country|lux_/.test(home.id), cls = homeClass(home);
  const base = { wall: 'default', floor: 'default', curtains: 'default', paint: 'default', gate: 'default', shutters: 'default', roof: 'default', door: 'default', garden: 'none', ceiling: 'default', finish: 'default', bath: 'default', kitchen: 'default', lighting: 'default', arrange: 'a' };
  return cls === 'upper'
    ? { ...base, sofa: 'lshape', bed: 'upholstered', curtains: 'blackout', rug: 'shaggy', tv: 'big', extras: ['art', 'plants'], generator: 'none' }
    : { ...base, sofa: big ? 'leather' : 'none', bed: home.id === 'face_me' ? 'mattress' : 'frame', rug: big ? 'red' : 'none', tv: 'small', extras: ['plants'], generator: 'none' };
}

/**
 * The home you're in now, if it's one of your homes abroad (its id): what Customise changes, and what homeStyleOf
 * gives, while you're in it. Not saved: set as you walk in, cleared as you leave.
 */
export function styleHere(s: LifeState, id: string | null) { (s as LifeState & { styling?: string | null }).styling = id; }
const stylingHome = (s: LifeState) => { const id = (s as LifeState & { styling?: string | null }).styling; return id ? homesAbroad(s).find((h) => h.id === id) ?? null : null; };

export function homeStyleOf(s: LifeState): HomeStyle {
  const away = stylingHome(s);
  if (away) {
    // (first time: as it came, furnished like your own, a few plants and prints; yours to change from there)
    if (!away.style) { styleHere(s, null); const main = homeStyleOf(s); styleHere(s, away.id); away.style = { ...structuredClone(main), paint: 'default', gate: 'default', garden: 'none', extras: ['plants', 'art'] }; }
    (away.style as RoomStyle).room = decorUp(s);
    return away.style;
  }
  if (!s.homeStyle) {
    const big = /mini_flat|family|lekki|lux_/.test(s.card.home.id), cls = homeClass(s.card.home);
    // how it came: a Lekki family home is furnished like one; a face-me room has a mattress on the floor
    s.homeStyle = cls === 'upper'
      ? { wall: 'default', floor: 'default', sofa: 'lshape', bed: 'upholstered', curtains: 'blackout', rug: 'shaggy', tv: 'big', extras: ['ac', 'art', 'plants'],
        paint: 'default', gate: 'default', generator: 'big', garden: 'interlock', shutters: 'default', roof: 'default', door: 'default', ceiling: 'default', finish: 'default', bath: 'default', kitchen: 'default', lighting: 'default', arrange: 'a' }
      : { wall: 'default', floor: 'default', sofa: big ? (cls === 'middle' ? 'leather' : 'fabric') : 'none', bed: s.card.home.id === 'face_me' ? 'mattress' : 'frame', curtains: 'default', rug: big ? 'red' : 'none', tv: big || s.card.region !== 'lagos' ? 'small' : 'none', /* (a studio flat abroad comes with a TV) */ extras: cls === 'middle' && big ? ['plants'] : [],
      paint: 'default', gate: 'default', shutters: 'default', roof: 'default', door: 'default', generator: s.card.background.id === 'wealthy' || s.card.background.id === 'comfortable' ? 'small' : 'none', garden: 'none',
      ceiling: 'default', finish: 'default', bath: 'default', kitchen: 'default', lighting: 'default', arrange: 'a' };
  }
  // saves from before the outside existed (and before the renovations)
  s.homeStyle.shutters ??= 'default'; s.homeStyle.roof ??= 'default'; s.homeStyle.door ??= 'default';
  s.homeStyle.paint ??= 'default'; s.homeStyle.gate ??= 'default'; s.homeStyle.generator ??= 'none'; s.homeStyle.garden ??= 'none';
  if (s.owned.includes('keyboard') && !s.homeStyle.extras.includes('keyboard')) s.homeStyle.extras.push('keyboard'); // (bought at the shop: it's yours here too)
  (s.homeStyle as RoomStyle).room = decorUp(s); // (the visitors' copy of your home has it too)
  s.homeStyle.ceiling ??= 'default'; s.homeStyle.finish ??= 'default'; s.homeStyle.bath ??= 'default'; s.homeStyle.kitchen ??= 'default'; s.homeStyle.lighting ??= 'default'; s.homeStyle.arrange ??= 'a';
  return s.homeStyle;
}

export const ownsHome = (s: LifeState) => !!s.homeOwned || !!stylingHome(s); // (a home abroad is bought: yours to change)

export function comfortOf(s: LifeState): number {
  const st = homeStyleOf(s);
  let c = 0;
  for (const k of ['wall', 'floor', 'sofa', 'bed', 'curtains', 'rug', 'tv', 'paint', 'gate', 'generator', 'garden', 'shutters', 'roof', 'door', 'ceiling', 'finish', 'bath', 'kitchen', 'lighting'] as const) c += STYLE[k].find((o) => o.id === st[k])?.comfort ?? 0;
  for (const e of st.extras) c += STYLE.extras.find((o) => o.id === e)?.comfort ?? 0;
  c += decorComfort(s);
  return c; // 0 .. ~60
}

/**
 * Storage: what you own that this home has no room for (a pool table in a flat; a sofa or a TV where there's no
 * space). It's kept, and comes out when you move somewhere it fits; or sell it, for half what it cost.
 */
export interface Stored { kind: 'extras' | 'sofa' | 'tv'; id: string; label: string; price: number }
export function storedHere(s: LifeState, fits: { sofa: boolean; tv: boolean; pool: boolean }): Stored[] {
  const st = homeStyleOf(s), out: Stored[] = [];
  const opt = (k: 'extras' | 'sofa' | 'tv', id: string) => STYLE[k].find((o) => o.id === id);
  if (!fits.pool && st.extras.includes('pool')) { const o = opt('extras', 'pool')!; out.push({ kind: 'extras', id: 'pool', label: 'Pool table', price: o.price }); }
  if (!fits.sofa && st.sofa !== 'none') { const o = opt('sofa', st.sofa); if (o) out.push({ kind: 'sofa', id: st.sofa, label: o.label, price: o.price }); }
  if (!fits.tv && st.tv !== 'none') { const o = opt('tv', st.tv); if (o) out.push({ kind: 'tv', id: st.tv, label: o.label, price: o.price }); }
  return out;
}
export function sellStored(s: LifeState, item: Stored): LifeEvent[] {
  const st = homeStyleOf(s), back = Math.round(item.price / 2 / 100) * 100;
  if (item.kind === 'extras') st.extras = st.extras.filter((x) => x !== item.id); else st[item.kind] = 'none';
  earn(s, back, `Sold: ${item.label}`);
  return [{ t: s.minutes, kind: 'info', text: `Sold the ${item.label.toLowerCase()} for ${money(s, back)}.` }];
}

/** Can you change this, and what will it cost? (null = allowed) */
export function styleBlock(s: LifeState, kind: keyof HomeStyle, opt: StyleOption): string | null {
  if (kind === 'bath' && !hasBathroom(s) && opt.id !== 'default') return 'A face-me-I-face-you room has no bathroom of its own.';
  if (opt.ownersOnly && !ownsHome(s)) return kind === 'wall' || kind === 'floor' || kind === 'finish' || kind === 'ceiling' ? 'Your landlord won\'t let you change the walls, floor or ceiling. Buy a place to do that.'
    : kind === 'bath' || kind === 'kitchen' ? 'Your landlord won\'t let you rip out the bathroom or kitchen. Buy a place to do that.' : 'It\'s the landlord\'s building. Buy a place to change the outside.';
  if (opt.price > s.money) return 'You can\'t afford that.';
  void kind;
  return null;
}

export function applyStyle(s: LifeState, kind: keyof HomeStyle, id: string): LifeEvent[] | null {
  const st = homeStyleOf(s);
  const opt = STYLE[kind].find((o) => o.id === id);
  if (!opt || styleBlock(s, kind, opt)) return null;
  if (kind === 'extras') {
    if (st.extras.includes(id)) return [];
    if (!spend(s, opt.price, opt.label)) return null;
    st.extras.push(id);
    if (id === 'keyboard' && !s.owned.includes('keyboard')) s.owned.push('keyboard'); // (the same keyboard the electricals shop sells)
  } else {
    if (st[kind] === id) return [];
    if (!spend(s, opt.price, opt.label)) return null;
    st[kind] = id;
  }
  if (opt.price > 100_000) s.history.push({ t: s.minutes, text: `Bought ${opt.label.toLowerCase()} for the house.` });
  return [{ t: s.minutes, kind: 'info', text: `${opt.label}. Home feels more like yours.` }];
}

/** Take one of the extras out of the room (it's in your way, or you've gone off it): no refund. */
export function removeExtra(s: LifeState, id: string): LifeEvent[] | null {
  const st = homeStyleOf(s), i = st.extras.indexOf(id);
  if (i < 0) return null;
  st.extras.splice(i, 1);
  if (id === 'keyboard') s.owned = s.owned.filter((o) => o !== 'keyboard'); // (the room puts the keyboard out for owning one)
  const opt = STYLE.extras.find((o) => o.id === id);
  return [{ t: s.minutes, kind: 'info', text: `${opt?.label ?? 'It'}: out of the room.` }];
}

// --- the market -------------------------------------------------------------------------------------

export interface Listing {
  /** not for sale: a real player's home you're visiting, furnished as they did it (their id, their name) */
  host?: { id: string; name: string; style: HomeStyle | null };
  id: string;
  layout: Layout;
  homeId: string; // the card home id this becomes
  label: string;
  area: string;
  rentYearly?: number;
  price?: number;
  note: string;
  /** whose rules the move-in follows (Lagos: a year up front) */
  country?: Country;
  /** whose market it's in (the Homes app browses them all): 'lagos', 'france', 'us', 'china', 'japan' */
  region?: MarketRegion;
}

/** The property markets you can browse in Homes (each country's own areas, money and rules). */
export type MarketRegion = 'lagos' | 'france' | 'us' | 'china' | 'japan' | 'italy' | 'uae';
export const MARKETS_LIST: { region: MarketRegion; country: Country; name: string; flag: string; currency: { symbol: string; code: string; rate?: number } }[] = [
  { region: 'lagos', country: 'nigeria', name: 'Nigeria', flag: '🇳🇬', currency: LAGOS.currency },
  { region: 'us', country: 'usa', name: 'United States', flag: '🇺🇸', currency: US.currency },
  { region: 'france', country: 'france', name: 'France', flag: '🇫🇷', currency: FRANCE.currency },
  { region: 'japan', country: 'japan', name: 'Japan', flag: '🇯🇵', currency: JAPAN.currency },
  { region: 'china', country: 'china', name: 'China', flag: '🇨🇳', currency: CHINA.currency },
  { region: 'italy', country: 'italy', name: 'Italy', flag: '🇮🇹', currency: ITALY.currency },
  { region: 'uae', country: 'uae', name: 'United Arab Emirates', flag: '🇦🇪', currency: UAE.currency },
];
export const marketOf = (region: string) => MARKETS_LIST.find((m) => m.region === region) ?? MARKETS_LIST[0];
/** the market your life is in (your home country's) */
export const homeMarket = (s: LifeState): MarketRegion => (MARKETS_LIST.some((m) => m.region === s.card.region) ? s.card.region : 'lagos') as MarketRegion;
/**
 * You can rent or buy where you live; elsewhere you look, save it, and dream (the money's real, the paperwork for a
 * foreigner isn't in the game yet: a home abroad is a goal, not a click).
 */
export const canMoveTo = (s: LifeState, l: Listing) => !l.host && (l.region ?? homeMarket(s)) === homeMarket(s);

const AREAS: Record<Layout, { label: string; homeId: string; rent: [string, number, number][]; buy: [string, number, number][] }> = {
  face_me: { label: 'Room in a face-me-I-face-you', homeId: 'face_me', rent: [['Mushin', 180_000, 260_000], ['Bariga', 200_000, 300_000], ['Ikotun', 160_000, 240_000]], buy: [] },
  self_contain: { label: 'Self-contain', homeId: 'self_contain', rent: [['Yaba', 750_000, 1_100_000], ['Akoka', 600_000, 900_000], ['Ogba', 550_000, 800_000]], buy: [['Ikorodu', 9_000_000, 14_000_000]] },
  mini_flat: { label: 'Mini flat', homeId: 'mini_flat', rent: [['Yaba', 1_600_000, 2_400_000], ['Surulere', 1_400_000, 2_000_000], ['Gbagada', 1_800_000, 2_600_000]], buy: [['Ikorodu', 18_000_000, 26_000_000], ['Surulere', 38_000_000, 52_000_000]] },
  family: { label: 'Three-bedroom flat', homeId: 'family_owned', rent: [['Surulere', 3_500_000, 5_000_000], ['Lekki', 7_000_000, 11_000_000]], buy: [['Gbagada', 85_000_000, 120_000_000], ['Lekki', 150_000_000, 220_000_000]] },
};

/** France (amounts in internal units: euros × 1,700); rent shown yearly like everywhere in the sim. */
const EU = (n: number) => Math.round(n * 1_700);
const FR_AREAS: Partial<Record<Layout, { label: string; homeId: string; rent: [string, number, number][]; buy: [string, number, number][] }>> = {
  self_contain: { label: 'Studio', homeId: 'self_contain', rent: [['Paris 7e', EU(11_000), EU(14_500)], ['Paris 11e', EU(9_600), EU(12_000)], ['Saint-Aurèle, Provence', EU(6_000), EU(7_800)]], buy: [['Saint-Aurèle, Provence', EU(95_000), EU(140_000)]] },
  mini_flat: { label: 'One-bedroom apartment', homeId: 'mini_flat', rent: [['Paris 7e', EU(17_000), EU(24_000)], ['Paris 15e', EU(14_400), EU(19_000)], ['Saint-Aurèle, Provence', EU(8_400), EU(11_000)]], buy: [['Paris 15e', EU(420_000), EU(560_000)], ['Saint-Aurèle, Provence', EU(160_000), EU(230_000)]] },
  family: { label: 'Family apartment', homeId: 'family_owned', rent: [['Paris 7e', EU(36_000), EU(54_000)], ['Saint-Aurèle, Provence', EU(14_000), EU(19_000)]], buy: [['Paris 7e', EU(1_400_000), EU(2_300_000)], ['Saint-Aurèle, Provence', EU(380_000), EU(650_000)]] },
};

type Areas = Partial<Record<Layout, { label: string; homeId: string; rent: [string, number, number][]; buy: [string, number, number][] }>>;

/**
 * The US, China, Japan: the market is the region's own homes (its areas, its rents), and a place to buy costs
 * what the rent says it's worth (price-to-rent: about 20 years in America, 25 in Japan, 45 in China's big cities).
 */
function areasFrom(c: RegionContent, years: number): Areas {
  const row = (id: string) => c.homes.find((h) => h.id === id);
  const out: Areas = {};
  for (const [layout, rentId, buyId] of [['self_contain', 'self_contain', 'self_contain'], ['mini_flat', 'mini_flat', 'mini_flat'], ['family', 'lekki_flat', 'family_owned']] as const) {
    const rent = row(rentId), own = row(buyId);
    if (!rent || !rent.rentYearly[1]) continue;
    const [lo, hi] = rent.rentYearly;
    out[layout] = { label: rent.label, homeId: rentId === 'lekki_flat' ? 'family_owned' : rentId, rent: rent.area.map((a) => [a, lo, hi]), buy: /^Room/.test(rent.label) ? [] : (own?.area ?? rent.area).map((a) => [a, lo * years, hi * years]) };
  }
  return out;
}
const MARKETS: Record<string, { areas: Areas; notes: string[]; buyNotes: string[] }> = {
  // (Italy and Dubai had no market: nothing to buy or rent there. Their own areas and rents; price-to-rent about 28 years in Italy, 16 in Dubai)
  italy: { areas: areasFrom(ITALY, 28), notes: ['Frescoed ceilings', 'Third floor, no lift', 'A balcony over the piazza', 'Near the tram', 'Freshly renovated', 'Shutters onto a courtyard'], buyNotes: ['Notary ready', 'Needs some work', 'Owners moving to Milan', 'Cellar included', 'View of the hills'] },
  uae: { areas: areasFrom(UAE, 16), notes: ['Pool and gym in the building', 'Chiller-free', 'Sea view', 'Near the metro', 'Freshly renovated', 'Covered parking'], buyNotes: ['Freehold', 'Off-plan, handed over', 'Owners relocating', 'Marina view', 'Golden visa eligible'] },
  us: { areas: areasFrom(US, 20), notes: ['Doorman building', 'Walk-up, fourth floor', 'Laundry in the basement', 'Near the subway', 'Freshly renovated', 'Rent-stabilised'], buyNotes: ['Co-op board approval', 'Needs some work', 'Owners moving to Florida', 'Parking included', 'Open house Sunday'] },
  china: { areas: areasFrom(CHINA, 45), notes: ['Near the metro', 'Lift, 18th floor', 'Freshly renovated', 'South-facing', 'Landlord is easy-going', 'Includes the furniture'], buyNotes: ['Clean title', 'Needs some work', 'School district', 'Owners emigrating', 'Underground parking'] },
  japan: { areas: areasFrom(JAPAN, 25), notes: ['Five minutes to the station', 'Auto-lock entrance', 'No key money', 'South-facing balcony', 'Pets allowed', 'Freshly renovated'], buyNotes: ['Built 2009', 'Needs some work', 'Owners relocating to Osaka', 'Near a good school', 'Parking space included'] },
};

/** This week's listings where you live (they change every week). */
export function listings(s: LifeState): Listing[] { return listingsIn(s, homeMarket(s)); }

/** This week's listings in any country's market (Homes lets you look anywhere). */
export function listingsIn(s: LifeState, region: MarketRegion): Listing[] {
  const week = Math.floor(dayOf(s.minutes) / 7);
  // (your own market keeps the sequence it always had; another country's has its own)
  const r = new Rng((s.seed ^ Math.imul(week + 77, 0x2c1b3c6d) ^ (region === homeMarket(s) ? 0 : [...region].reduce((h, c) => Math.imul(h, 31) + c.charCodeAt(0), 7))) >>> 0);
  const out: Listing[] = [];
  const m = marketOf(region), country = m.country;
  const fr = region === 'france', mk = MARKETS[region];
  const table: Areas = fr ? FR_AREAS : mk ? mk.areas : AREAS;
  // every range on the market, not a pick: each area of each kind of home, to rent and to buy (the prices move week to week)
  // (rounded to a figure people say in the local money: $27,200 a year, not $27,181)
  const rate = m.currency.rate ?? 1, step = rate === 1 ? 10_000 : rate * 10 ** Math.ceil(Math.log10(100_000 / rate));
  const rentNotes = mk ? mk.notes : fr ? ['Parquet floors', 'Sixth floor, no lift', 'A balcony over the street', 'Near the Métro', 'Freshly renovated', 'The concierge is lovely'] : ['Newly painted', 'Prepaid meter', 'Constant water', 'Gated compound', 'Close to the bus stop', 'The landlord lives upstairs'];
  const buyNotes = mk ? mk.buyNotes : fr ? ['Notaire ready', 'Needs some work', 'Owners moving to Lyon', 'Cellar included', 'View of the hills'] : ['C of O available', 'Governor’s consent', 'Estate with security', 'Needs some work', 'Family is relocating abroad'];
  for (const layout of Object.keys(table) as Layout[]) {
    const a = table[layout]!;
    a.rent.forEach(([area, lo, hi], i) => out.push({ country, region, id: `w${week}-${region}-${layout}-rent-${i}`, layout, homeId: a.homeId, label: a.label, area, rentYearly: Math.round(r.range(lo, hi) / step) * step, note: r.pick(rentNotes) }));
    a.buy.forEach(([area, lo, hi], i) => out.push({ country, region, id: `w${week}-${region}-${layout}-buy-${i}`, layout, homeId: a.homeId, label: a.label, area, price: Math.round(r.range(lo, hi) / (step * 10)) * step * 10, note: r.pick(buyNotes) }));
  }
  // Lagos at the top: a detached house in a gated estate (the mansion behind its own wall)
  if (region === 'lagos') for (const [i, [area, lo, hi]] of ([['Ikoyi', 900_000_000, 1_600_000_000], ['Banana Island', 1_800_000_000, 3_200_000_000], ['Victoria Garden City', 450_000_000, 750_000_000]] as const).entries())
    out.push({ country, region, id: `w${week}-lagos-estate-buy-${i}`, layout: 'family', homeId: 'family_estate', label: 'Two-storey house with a pool and garage', area, price: Math.round(r.range(lo, hi) / 10_000_000) * 10_000_000, note: r.pick(['Gated estate, 24-hour security', 'A glass wall onto the pool', 'Double-height living room, a gallery', 'Five bedrooms, all en suite', 'Governor’s consent, clean title']) });
  // America: a white colonial house on twelve acres in the Lowcountry, always on the market (you can paint it, change
  // its shutters, its roof and its front door: Customise, outside)
  if (region === 'us') out.push({ country, region, id: `w${week}-us-country-buy`, layout: 'family', homeId: 'country_house', label: 'Colonial house with a porch', area: 'Seabrook, South Carolina',
    price: Math.round(r.range(1_750_000, 2_150_000) / 10_000) * 10_000 * USD_NGN, note: r.pick(['Two-storey porch, twelve acres, oaks', 'Built 1801, restored', 'A balcony right round the top', 'The old Calhoun place', 'Down a lane by the river']) });
  // one luxury house in each of the other countries, each its own (inside: life/luxury.ts; outside: luxhomes.ts)
  const LUX: Partial<Record<MarketRegion, [string, string, string, number, number, string[]]>> = {
    us: ['lux_us', 'Brutalist house with a glass wall', 'Pacific Heights, San Francisco', 8_500_000, 11_500_000, ['Raw concrete, a wall of glass onto the hills', 'A concrete hearth, a sunken lounge', 'Architect-built, published in the magazines', 'Two storeys, a gallery over the living room']],
    france: ['lux_paris', 'Penthouse with a terrace', 'Avenue Montaigne, Paris 8e', 8_000_000, 12_000_000, ['The Eiffel Tower from the terrace', 'Gilded panelling, crystal chandelier', 'A doorman, a red carpet', 'Double height, a gallery']],
    japan: ['lux_japan', 'Architect-designed house with a garden', 'Minami-Azabu, Tokyo', 900_000_000, 1_300_000_000, ['A glass wall onto a dry garden', 'Hinoki, cedar slats, an engawa', 'A maple in the garden', 'Quiet street, near the embassies']],
    china: ['lux_china', 'Sky penthouse over the river', 'The Bund, Shanghai', 90_000_000, 140_000_000, ['The whole city at your feet', 'The TV tower out of the window', 'Marble, a chandelier, a gallery', 'Concierge, a private lift']],
  };
  // (France's other: a house, not a flat. "A special house in each country": the penthouse is an apartment)
  if (region === 'france') out.push({ country, region, id: `w${week}-france-mas-buy`, layout: 'family', homeId: 'lux_provence', label: 'Stone mas with lavender fields', area: 'Saint-Aurèle, Provence',
    price: Math.round((r.range(2_400_000, 3_600_000) * rate) / (step * 10)) * step * 10, note: r.pick(['Two storeys of old stone, a vine pergola', 'Rows of lavender, two cypresses', 'Blue shutters, a terracotta roof', 'An arched oak door, a terrace in the shade']) });
  const lux = LUX[region];
  if (lux) { const [homeId, label, area, lo, hi, notes] = lux; out.push({ country, region, id: `w${week}-${region}-lux-buy`, layout: 'family', homeId, label, area, price: Math.round((r.range(lo, hi) * rate) / (step * 10)) * step * 10, note: r.pick(notes) }); }
  for (const l of out) { if (l.rentYearly) l.rentYearly = Math.max(step, Math.round(l.rentYearly * PRICES / step) * step); if (l.price) l.price = Math.max(step * 10, Math.round(l.price * PRICES / (step * 10)) * step * 10); } // (prices 30% down, prices.ts; still a figure people say in the local money)
  return out;
}

/** Homes you've saved (a snapshot: the listing as it was; listings change every week). Up to 12. */
export function savedHomes(s: LifeState): Listing[] { return (s as LifeState & { savedHomes?: Listing[] }).savedHomes ?? []; }
export const isSaved = (s: LifeState, l: Listing) => savedHomes(s).some((x) => x.id === l.id);
export function toggleSaved(s: LifeState, l: Listing): boolean {
  const st = s as LifeState & { savedHomes?: Listing[] };
  const list = (st.savedHomes ??= []);
  const i = list.findIndex((x) => x.id === l.id);
  if (i >= 0) { list.splice(i, 1); return false; }
  list.unshift({ ...l });
  if (list.length > 12) list.length = 12;
  return true;
}

/** What moving in costs, by the local rules: Lagos wants a year up front plus agency and legal; elsewhere it's months. */
/** a home you own besides the one you live in: bought in another country (your place there when you're in that city;
 *  your life stays at home), or one you owned and moved out of (`kept`: you can move back, or sell it) */
export interface HomeAbroad { id: string; region: MarketRegion; homeId: string; label: string; area: string; price: number; bought: number; kept?: boolean; /** done up as you like (Customise, while you're in it) */ style?: HomeStyle }
export const homesAbroad = (s: LifeState): HomeAbroad[] => (s as LifeState & { homesAbroad?: HomeAbroad[] }).homesAbroad ?? [];
/**
 * Buy a home abroad (you're there, on a trip: you've seen it): the price and that country's closing costs, paid now;
 * it's yours, and when you're in that city it's where you go home to (not a hotel). Your life stays where it is.
 */
export function buyAbroad(s: LifeState, l: Listing): { ok: boolean; line: string } {
  if (!l.price || l.host) return { ok: false, line: 'Only a home for sale can be bought.' };
  if (homesAbroad(s).some((h) => h.id === l.id)) return { ok: false, line: 'It\'s already yours.' };
  // (its country decides where it is: a listing without a region used to be filed as a Lagos home, and was never found)
  const region = (l.region ?? MARKETS_LIST.find((m) => m.country === l.country)?.region) as MarketRegion | undefined;
  if (!region) return { ok: false, line: 'This listing doesn\'t say which country it\'s in, so it can\'t be bought.' };
  const cost = moveInCost(l);
  if (!spend(s, cost.total, `Bought ${l.label.toLowerCase()}, ${l.area}`)) return { ok: false, line: 'You don\'t have enough for that yet.' };
  const st = s as LifeState & { homesAbroad?: HomeAbroad[] };
  (st.homesAbroad ??= []).push({ id: l.id, region, homeId: l.homeId, label: l.label, area: l.area, price: l.price, bought: s.minutes });
  s.history.push({ t: s.minutes, text: `Bought a home abroad: ${l.label.toLowerCase()}, ${l.area}.` });
  return { ok: true, line: `It's yours: ${l.label}, ${l.area}. Whenever you're in this city, home is here.` };
}

/**
 * (old saves) A home abroad filed under the wrong country: its listing id says which market it came from
 * (w<week>-<region>-…); put right, so it's found when you're there. Nothing else about it changes. Returns how many.
 */
export function healHomesAbroad(s: LifeState): number {
  let n = 0;
  for (const h of homesAbroad(s)) {
    const m = /^w\d+-(lagos|us|france|japan|china)-/.exec(h.id), want = m?.[1] as MarketRegion | undefined;
    if (want && h.region !== want) { h.region = want; n++; }
  }
  return n;
}
/** the home you live in, put by as one you own (moving out of it) */
function keepHome(s: LifeState) {
  const st = s as LifeState & { homesAbroad?: HomeAbroad[] }, h = s.card.home;
  (st.homesAbroad ??= []).push({ id: `kept-${s.minutes}-${h.id}`, region: (s.card.region ?? 'lagos') as MarketRegion, homeId: h.id, label: h.label, area: h.area, price: s.homeValue, bought: s.minutes, kept: true });
}
/** Sell a home you own and don't live in: for a bit less than it cost. */
export function sellHome(s: LifeState, id: string): { ok: boolean; line: string } {
  const st = s as LifeState & { homesAbroad?: HomeAbroad[] }, i = homesAbroad(s).findIndex((h) => h.id === id);
  if (i < 0) return { ok: false, line: 'That isn\'t yours.' };
  const [h] = st.homesAbroad!.splice(i, 1), back = Math.round(h.price * 0.9);
  earn(s, back, `Sold ${h.label.toLowerCase()}, ${h.area}`);
  s.history.push({ t: s.minutes, text: `Sold ${h.label.toLowerCase()} in ${h.area}.` });
  return { ok: true, line: `Sold: ${h.label}, ${h.area}.` };
}
/** Move back into a home you own in the country you live in: nothing to pay; the place you leave stays yours if you own it. */
export function moveBack(s: LifeState, id: string): { ok: boolean; line: string; events: LifeEvent[] } {
  const st = s as LifeState & { homesAbroad?: HomeAbroad[] }, i = homesAbroad(s).findIndex((h) => h.id === id);
  if (i < 0) return { ok: false, line: 'That isn\'t yours.', events: [] };
  const h = st.homesAbroad![i];
  if (h.region !== homeMarket(s)) return { ok: false, line: 'Your life is in another country: that one\'s for your trips.', events: [] };
  st.homesAbroad!.splice(i, 1);
  if (s.homeOwned && s.homeValue) keepHome(s);
  s.card.home = { ...s.card.home, id: h.homeId, label: h.label, area: h.area, rentYearly: 0, rentDueMonths: 12, note: 'Yours' };
  s.rent = null;
  s.homeOwned = true;
  s.homeValue = h.price;
  const sty = homeStyleOf(s);
  sty.paint = 'default'; sty.gate = 'default'; sty.garden = 'none'; sty.shutters = 'default'; sty.roof = 'default'; sty.door = 'default';
  s.history.push({ t: s.minutes, text: `Moved back into the ${h.label.toLowerCase()} in ${h.area}.` });
  return { ok: true, line: `Home again: ${h.label}, ${h.area}.`, events: advance(s, 4 * 60) };
}

export function moveInCost(l: Listing): { total: number; parts: [string, number][] } {
  const c = l.country ?? 'nigeria';
  const r1k = (n: number) => Math.round(n / 1000) * 1000;
  if (l.price && c !== 'nigeria') {
    // closing costs: notaire fees in France, closing in the US, deed tax in China, agent + registration in Japan
    const fees: Record<Country, [string, number][]> = { nigeria: [], france: [['Notaire fees (7.5%)', 0.075]], usa: [['Closing costs (3%)', 0.03]], china: [['Deed tax (3%)', 0.03], ['Agency (1%)', 0.01]], japan: [['Agent (3%)', 0.03], ['Registration (2%)', 0.02]], italy: [['Notaio and taxes (9%)', 0.09], ['Agency (3%)', 0.03]], uae: [['Land Department fee (4%)', 0.04], ['Agency (2%)', 0.02]] };
    const parts: [string, number][] = [['Price', l.price], ...fees[c].map(([k, f]) => [k, r1k(l.price! * f)] as [string, number])];
    return { total: parts.reduce((a, [, v]) => a + v, 0), parts };
  }
  if (!l.price && c !== 'nigeria') {
    const m = l.rentYearly! / 12;
    const rules: Record<Country, [string, number][]> = { nigeria: [], france: [['First month', 1], ['Deposit (1 month)', 1], ['Agency (1 month)', 1]], usa: [['First month', 1], ['Security deposit', 1], ['Broker fee (1 month)', 1]], china: [['Deposit (1 month)', 1], ['Three months up front', 3], ['Agency (1 month)', 1]], japan: [['First month', 1], ['Deposit (shikikin)', 1], ['Key money (reikin)', 1], ['Agency (1 month)', 1]], italy: [['First month', 1], ['Deposit (cauzione, 3 months)', 3], ['Agency (1 month)', 1]], uae: [['First rent cheque (3 months)', 3], ['Deposit (5% of the year)', 0.6], ['Agency (5% of the year)', 0.6]] };
    const parts: [string, number][] = rules[c].map(([k, n]) => [k, r1k(m * n)]);
    return { total: parts.reduce((a, [, v]) => a + v, 0), parts };
  }
  if (l.price) {
    const legal = Math.round(l.price * 0.05 / 1000) * 1000, agency = Math.round(l.price * 0.05 / 1000) * 1000;
    return { total: l.price + legal + agency, parts: [['Price', l.price], ['Legal (5%)', legal], ['Agency (5%)', agency]] };
  }
  const rent = l.rentYearly!, agency = Math.round(rent * 0.1 / 1000) * 1000, legal = Math.round(rent * 0.1 / 1000) * 1000;
  return { total: rent + agency + legal, parts: [['One year rent', rent], ['Agency (10%)', agency], ['Legal (10%)', legal]] };
}

/** Take it: pay, move your things, and from tonight this is home. */
export function moveIn(s: LifeState, l: Listing): { ok: boolean; line: string; events: LifeEvent[] } {
  const cost = moveInCost(l);
  if (!spend(s, cost.total, l.price ? `Bought ${l.label.toLowerCase()}, ${l.area}` : `Moved: ${l.label.toLowerCase()}, ${l.area}`)) return { ok: false, line: 'You don\'t have enough for that yet.', events: [] };
  // if you owned the old place, it stays yours (Home lists it: move back into it, or sell it)
  if (s.homeOwned && s.homeValue) keepHome(s);
  s.card.home = { ...s.card.home, id: l.homeId, label: l.label, area: l.area, rentYearly: l.rentYearly ?? 0, rentDueMonths: 12, note: l.note };
  // (Lagos: the year is paid; elsewhere the first month is, and the next is due in a month)
  s.rent = l.rentYearly ? { yearly: l.rentYearly, dueDay: dayOf(s.minutes) + ((l.country ?? 'nigeria') === 'nigeria' ? 365 : 30), warned: false } : null;
  s.homeOwned = !!l.price;
  s.homeValue = l.price ?? 0;
  const st = homeStyleOf(s);
  if (!s.homeOwned) { st.wall = 'default'; st.floor = 'default'; } // a new landlord's walls
  st.paint = 'default'; st.gate = 'default'; st.garden = 'none'; st.shutters = 'default'; st.roof = 'default'; st.door = 'default'; // a different building (the generator comes with you)
  s.history.push({ t: s.minutes, text: l.price ? `Bought a ${l.label.toLowerCase()} in ${l.area}.` : `Moved into a ${l.label.toLowerCase()} in ${l.area}.` });
  if (l.price) nudge(s, 'ambitious', 0.05);
  const events = advance(s, 6 * 60); // moving day
  return { ok: true, line: l.price ? `It's yours. The keys are heavier than you expected.` : 'You pay, sign, and spend the day moving your things in a borrowed bus.', events };
}
